import { useCallback, useEffect, useState } from "react";
import { Wallet, AlertTriangle, Save, TrendingUp, Cpu, Layers } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat, Bar } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const RATE_LABELS = { llm_call: "AI text call", image: "Generated image", email: "Email sent", research: "Research query" };

function monthOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) { const m = new Date(d.getFullYear(), d.getMonth() - i, 1); out.push(m.toISOString().slice(0, 7)); }
  return out;
}

export default function FinOps() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [budget, setBudget] = useState("");
  const [alertPct, setAlertPct] = useState("");
  const [rates, setRates] = useState({});
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState(null);
  const load = useCallback((m) => {
    api.get(`/finops/overview?month=${m}`).then(({ data }) => {
      setData(data); setBudget(data.budget); setAlertPct(data.alert_pct); setRates(data.rates);
    });
    api.get(`/owner/finops/customers?month=${m}`).then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);
  useEffect(() => { load(month); }, [load, month]);
  if (!data) return <Loading />;

  const maxDaily = Math.max(1, ...data.daily.map((d) => d.cost));
  const save = async () => {
    setSaving(true);
    try { await api.post("/finops/settings", { budget: Number(budget), alert_pct: Number(alertPct), rates: Object.fromEntries(Object.entries(rates).map(([k, v]) => [k, Number(v)])) }); toast.success("FinOps settings saved"); load(month); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); } finally { setSaving(false); }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="finops-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Wallet size={22} className="text-[#b0862f]" /> FinOps / Cost Control</h1>
          <p className="mt-1 text-[13px] text-slate-500">Estimated spend across AI, images, email and research — from real usage, using your rate card.</p>
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="finops-month" className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]">
          {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {data.alert && (
        <div className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-[13px] font-semibold ${data.over_budget ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid="finops-alert">
          <AlertTriangle size={16} /> {data.over_budget ? `Over budget — estimated $${data.totals.cost} exceeds your $${data.budget} monthly budget.` : `Heads up — you're at ${data.used_pct}% of your $${data.budget} monthly budget.`}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        <Card className="p-4"><div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Estimated spend</div><div className="mt-1 font-head text-2xl font-extrabold tabular-nums text-slate-900" data-testid="finops-total">${data.totals.cost}</div><div className="font-mono text-[10px] text-slate-400">estimated</div></Card>
        <Stat label="Billable events" value={data.totals.events} tone="blue" />
        <Card className="p-4"><div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Monthly budget</div><div className="mt-1 font-head text-2xl font-extrabold tabular-nums text-slate-900">${data.budget}</div></Card>
        <Card className="p-4"><div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Budget used</div><div className={`mt-1 font-head text-2xl font-extrabold tabular-nums ${data.over_budget ? "text-red-600" : data.alert ? "text-amber-600" : "text-emerald-600"}`}>{data.used_pct}%</div></Card>
      </div>

      <Card className="p-5">
        <div className="mb-2 flex items-center justify-between text-[12px]"><span className="font-mono uppercase tracking-wider text-slate-400">Budget usage</span><span className="text-slate-500">${data.totals.cost} of ${data.budget}</span></div>
        <Bar pct={data.used_pct} tone={data.over_budget ? "#dc2626" : data.alert ? "#d97706" : "#1a5fd0"} height={10} />
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5" data-testid="finops-by-feature">
          <div className="mb-3 flex items-center gap-2"><Layers size={15} className="text-[#1a5fd0]" /><span className="font-head text-[14px] font-bold text-slate-900">Spend by feature</span></div>
          {data.by_feature.length === 0 ? <div className="py-6 text-center text-[13px] text-slate-400">No usage this month.</div> : (
            <div className="space-y-2.5">
              {data.by_feature.map((f) => (
                <div key={f.feature}>
                  <div className="mb-1 flex items-center justify-between text-[13px]"><span className="text-slate-700">{f.feature} <span className="text-slate-400">· {f.events}</span></span><span className="font-mono text-slate-600">${f.cost}</span></div>
                  <Bar pct={data.totals.cost ? (f.cost / data.totals.cost) * 100 : 0} tone="#b0862f" />
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5" data-testid="finops-by-model">
          <div className="mb-3 flex items-center gap-2"><Cpu size={15} className="text-[#b0862f]" /><span className="font-head text-[14px] font-bold text-slate-900">Spend by model / provider</span></div>
          {data.by_model.length === 0 ? <div className="py-6 text-center text-[13px] text-slate-400">No usage this month.</div> : (
            <div className="space-y-1.5">
              {data.by_model.map((m) => (
                <div key={m.model} className="flex items-center justify-between text-[13px]"><span className="truncate font-mono text-[12px] text-slate-600">{m.model}</span><span className="shrink-0 font-mono text-slate-600">{m.events} · ${m.cost}</span></div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5" data-testid="finops-daily">
        <div className="mb-3 flex items-center gap-2"><TrendingUp size={15} className="text-[#1a5fd0]" /><span className="font-head text-[14px] font-bold text-slate-900">Daily spend</span></div>
        {data.daily.length === 0 ? <div className="py-6 text-center text-[13px] text-slate-400">No usage this month.</div> : (
          <div className="flex items-end gap-1.5" style={{ height: 120 }}>
            {data.daily.map((d) => (
              <div key={d.date} className="group relative flex-1" title={`${d.date}: $${d.cost}`}>
                <div className="w-full rounded-t bg-[#1a5fd0]/80 transition-all hover:bg-[#1a5fd0]" style={{ height: `${(d.cost / maxDaily) * 100}%`, minHeight: 2 }} />
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 font-mono text-[10px] text-slate-400">{data.daily.length} active day(s) this month</div>
      </Card>

      <Card className="p-5" data-testid="finops-customers">
        <div className="mb-3 flex items-center justify-between">
          <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Estimated cost per customer · {month}</div>
          {customers && <span className="font-mono text-[11px] text-slate-500">Total ${customers.total_cost}</span>}
        </div>
        {!customers || customers.customers.length === 0 ? <div className="text-[13px] text-slate-400">No attributable usage this month.</div> : (
          <div className="divide-y divide-slate-100">
            {customers.customers.map((c) => (
              <div key={c.user_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[12px]" data-testid="finops-customer-row">
                <span className="w-56 truncate font-semibold text-slate-800">{c.name} <span className="font-normal text-slate-400">· {c.role}</span></span>
                <span className="flex-1 truncate text-slate-500">{Object.entries(c.features).map(([k, v]) => `${k} ×${v}`).join(" · ")}</span>
                <span className="font-mono text-slate-600">{c.events} ev</span>
                <span className="w-16 text-right font-mono font-bold text-slate-900">${c.cost}</span>
              </div>
            ))}
          </div>
        )}
        {customers && <div className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-400">{customers.note}</div>}
      </Card>

      <Card className="p-5" data-testid="finops-settings">
        <div className="mb-4 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Budget, alerts & rate card</div>
        <div className="grid items-end gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="block"><span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">Monthly budget ($)</span>
            <input type="number" min={0} value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="finops-budget" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" /></label>
          <label className="block"><span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">Alert at (% of budget)</span>
            <input type="number" min={1} max={100} value={alertPct} onChange={(e) => setAlertPct(e.target.value)} data-testid="finops-alert-pct" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" /></label>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.keys(RATE_LABELS).map((k) => (
            <label key={k} className="block"><span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">{RATE_LABELS[k]} ($/ea)</span>
              <input type="number" step="0.001" min={0} value={rates[k] ?? ""} onChange={(e) => setRates({ ...rates, [k]: e.target.value })} data-testid={`finops-rate-${k}`} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" /></label>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid="finops-save-btn" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-5 py-2.5 font-head text-[14px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60"><Save size={15} /> {saving ? "Saving…" : "Save settings"}</button>
      </Card>
    </div>
  );
}
